// Unit conversion utilities for recipe measurements

// Conversion factors
const CONVERSIONS = {
  // Weight — these are international, no regional variation.
  gToOz: 0.035274,
  ozToG: 28.3495,
  kgToLb: 2.20462,
  lbToKg: 0.453592,

  // Volume — AUSTRALIAN metric measures, because that is what our users' measuring
  // equipment actually is: cup = 250 mL, tablespoon = 20 mL, teaspoon = 5 mL.
  // Do NOT "correct" these to the US values (cup 236.588 mL, tbsp 14.7868 mL).
  // The 20 mL tablespoon is specific to Australia — the UK and NZ use 15 mL — so an
  // AU tablespoon is a third larger than a US one and getting it wrong is a 26% error.
  // fl oz stays US (29.5735 mL) as it only ever appears when parsing US recipes.
  flOzToMl: 29.5735,
  lToCup: 4,
  cupToL: 0.25,
  mlToTbsp: 0.05,
  tbspToMl: 20,
  mlToTsp: 0.2,
  tspToMl: 5,

  // Temperature
  cToF: (c) => (c * 9/5) + 32,
  fToC: (f) => (f - 32) * 5/9,
};

// Parse quantity string to extract number and unit
function parseQuantity(quantityStr) {
  const trimmed = quantityStr.trim();
  
  // Handle fractions like "1/2", "1 1/2"
  const fractionMatch = trimmed.match(/^(\d+\s+)?(\d+)\/(\d+)\s*(.*)$/);
  if (fractionMatch) {
    const whole = fractionMatch[1] ? parseInt(fractionMatch[1]) : 0;
    const numerator = parseInt(fractionMatch[2]);
    const denominator = parseInt(fractionMatch[3]);
    const decimal = whole + (numerator / denominator);
    const rest = fractionMatch[4];
    
    // Extract unit from rest
    const unitMatch = rest.match(/^([a-zA-Z]+)\s*(.*)$/);
    if (unitMatch) {
      return { amount: decimal.toString(), unit: unitMatch[1].toLowerCase(), rest: unitMatch[2] };
    }
    return { amount: decimal.toString(), unit: '', rest };
  }
  
  // Handle decimal or whole numbers
  const match = trimmed.match(/^([\d.]+)\s*([a-zA-Z]*)\s*(.*)$/);
  if (match) {
    return {
      amount: match[1],
      unit: match[2].toLowerCase(),
      rest: match[3]
    };
  }
  
  // No number found, return as is
  return { amount: '', unit: '', rest: trimmed };
}

// Format a number to a readable fraction or decimal
function formatNumber(num) {
  // Common fractions
  const fractions = {
    '0.25': '1/4',
    '0.33': '1/3',
    '0.5': '1/2',
    '0.66': '2/3',
    '0.75': '3/4',
  };
  
  const decimal = num % 1;
  const whole = Math.floor(num);
  
  if (decimal === 0) {
    return whole.toString();
  }
  
  const decimalStr = decimal.toFixed(2);
  if (fractions[decimalStr]) {
    return whole > 0 ? `${whole} ${fractions[decimalStr]}` : fractions[decimalStr];
  }
  
  // Round to 1 decimal place for display
  return num.toFixed(1).replace(/\.0$/, '');
}

// Convert a single ingredient quantity
export function convertIngredientQuantity(quantity, targetSystem) {
  const parsed = parseQuantity(quantity);
  
  if (!parsed.amount || !parsed.unit) {
    // No unit to convert, return as is
    return quantity;
  }
  
  const amount = parseFloat(parsed.amount);
  const unit = parsed.unit;
  let converted = null;
  
  // Metric to Imperial conversions
  if (targetSystem === 'imperial') {
    switch (unit) {
      case 'g':
      case 'gram':
      case 'grams':
        if (amount >= 454) {
          // Convert to pounds if over 1 lb
          converted = { value: amount * CONVERSIONS.gToOz / 16, unit: 'lb' };
        } else {
          converted = { value: amount * CONVERSIONS.gToOz, unit: 'oz' };
        }
        break;
      case 'kg':
      case 'kilogram':
      case 'kilograms':
        converted = { value: amount * CONVERSIONS.kgToLb, unit: 'lb' };
        break;
      case 'ml':
      case 'milliliter':
      case 'milliliters':
        if (amount >= CONVERSIONS.cupToL * 1000) {
          // Convert to cups if >= 1 cup (250 mL). Derived from the factor rather than
          // hardcoded so the threshold can never drift out of step with it.
          converted = { value: amount / (CONVERSIONS.cupToL * 1000), unit: 'cup' };
        } else if (amount >= CONVERSIONS.tbspToMl) {
          // Convert to tbsp if >= 1 tbsp (20 mL)
          converted = { value: amount * CONVERSIONS.mlToTbsp, unit: 'tbsp' };
        } else {
          converted = { value: amount * CONVERSIONS.mlToTsp, unit: 'tsp' };
        }
        break;
      case 'l':
      case 'liter':
      case 'liters':
      case 'litre':
      case 'litres':
        converted = { value: amount * CONVERSIONS.lToCup, unit: 'cup' };
        break;
      default:
        break;
    }
  }
  
  // Imperial to Metric conversions
  if (targetSystem === 'metric') {
    switch (unit) {
      case 'oz':
      case 'ounce':
      case 'ounces':
        converted = { value: amount * CONVERSIONS.ozToG, unit: 'g' };
        break;
      case 'lb':
      case 'lbs':
      case 'pound':
      case 'pounds':
        if (amount >= 1) {
          converted = { value: amount * CONVERSIONS.lbToKg, unit: 'kg' };
        } else {
          converted = { value: amount * CONVERSIONS.lbToKg * 1000, unit: 'g' };
        }
        break;
      case 'cup':
      case 'cups':
        converted = { value: amount * CONVERSIONS.cupToL * 1000, unit: 'ml' };
        break;
      case 'tbsp':
      case 'tablespoon':
      case 'tablespoons':
        converted = { value: amount * CONVERSIONS.tbspToMl, unit: 'ml' };
        break;
      case 'tsp':
      case 'teaspoon':
      case 'teaspoons':
        converted = { value: amount * CONVERSIONS.tspToMl, unit: 'ml' };
        break;
      case 'fl oz':
      case 'floz':
      case 'fluid ounce':
      case 'fluid ounces':
        converted = { value: amount * CONVERSIONS.flOzToMl, unit: 'ml' };
        break;
      default:
        break;
    }
  }
  
  // If conversion was performed, format the result
  if (converted) {
    const formattedAmount = formatNumber(converted.value);
    return `${formattedAmount}${converted.unit}${parsed.rest ? ' ' + parsed.rest : ''}`;
  }
  
  // No conversion needed or unit not recognized, return original
  return quantity;
}

// Convert all ingredients in a recipe
export function convertIngredients(ingredients, targetSystem) {
  return ingredients.map(ingredient => ({
    name: ingredient.name,
    quantity: convertIngredientQuantity(ingredient.quantity, targetSystem)
  }));
}

// Convert temperature in method steps (e.g., "180°C" to "356°F")
export function convertMethodTemperatures(method, targetSystem) {
  return method.map(step => {
    if (targetSystem === 'imperial') {
      // Convert Celsius to Fahrenheit
      return step.replace(/(\d+)\s*°?\s*C/gi, (match, temp) => {
        const fahrenheit = Math.round(CONVERSIONS.cToF(parseInt(temp)));
        return `${fahrenheit}°F`;
      });
    } else {
      // Convert Fahrenheit to Celsius
      return step.replace(/(\d+)\s*°?\s*F/gi, (match, temp) => {
        const celsius = Math.round(CONVERSIONS.fToC(parseInt(temp)));
        return `${celsius}°C`;
      });
    }
  });
}
